import { documentReady } from "@sazka/utils/document-ready";
import Odometer from "odometer";
import { CasinoJackpot, CasinoJackpotType } from "./casino-jackpot";
import { watch } from "vue";
import useJackpotManager from "./use-jackpot-manager";
import { StringUtils } from "@sazka/utils/string-utils";

const rootSelector = ".jp-odometer__root";
const itemsContainerSelector = ".jp-odometer__items-container";
const itemSelector = ".jp-odometer__item";
const totalSelector = ".jp-odometer__total";
const prizeValueSelector = ".jp-odometer__prize-value";

const initAllOdometers = () => {
    const odometerElements = document.querySelectorAll<HTMLElement>(prizeValueSelector);
    odometerElements.forEach((element) => {
        const oldPrize = element.dataset.prize;

        const odometer = new Odometer({
            el: element,
            value: oldPrize,
            duration: 4000,
            format: "( ddd)",
        });

        odometer.render();
    });
};

const updateJackpot = (jackpotElement: HTMLElement, newPrize: number) => {
    const odometerElement = jackpotElement.querySelector<HTMLElement>(prizeValueSelector);
    if (!environment.isLowPerformanceDevice) {
        odometerElement.innerHTML = newPrize.toString();
    } else {
        odometerElement.innerHTML = StringUtils.formatNumber(Math.round(newPrize), true);
    }
};

const jackpotLoadedCallback = (jackpotsContainer: HTMLElement, data: CasinoJackpot[]) => {
    if (!data || !Array.isArray(data) || data.length == 0) {
        return;
    }

    data.forEach((jackpot) => {
        const jackpotPrize = jackpot.prize;
        const jackpotElement = jackpotsContainer.querySelector<HTMLElement>(
            `${itemSelector}--${jackpot.name.toLowerCase()}`
        );

        if (jackpotElement) {
            updateJackpot(jackpotElement, jackpotPrize);
        }
    });
};

const getTotal = async (elements: HTMLElement[]) => {
    const { jackpots } = await useJackpotManager(["total"]);
    watch(
        () => jackpots.totalJackpot,
        (newTotalJackpot) => {
            if (newTotalJackpot) {
                elements.forEach((element) => {
                    updateJackpot(element, newTotalJackpot);
                });
            }
        }
    );
};

const getJackpots = async (root: HTMLElement) => {
    const jackpotsContainers = root.querySelectorAll<HTMLElement>(itemsContainerSelector);
    const providers = [...jackpotsContainers].map((jackpotsContainer) => {
        return jackpotsContainer.dataset.jackpotProvider as CasinoJackpotType;
    });

    const { jackpots } = await useJackpotManager(providers);

    for (const provider of providers) {
        const safeProvider = provider.toLowerCase() as CasinoJackpotType;
        watch(
            () => jackpots.jackpotsByProvider[safeProvider],
            (newJackpots) => {
                const jackpotsContainer = root.querySelector<HTMLElement>(
                    `${itemsContainerSelector}[data-jackpot-provider="${provider}"]`
                );
                jackpotLoadedCallback(jackpotsContainer, [...newJackpots]);
            },
            { deep: true }
        );
    }
};

const initJackpots = () => {
    const jackpotsRoots = document.querySelectorAll<HTMLElement>(rootSelector);
    const totalJackpotElements = document.querySelectorAll<HTMLElement>(totalSelector);

    if ((jackpotsRoots.length || totalJackpotElements.length) && !environment.isLowPerformanceDevice) {
        initAllOdometers();
    }

    if (totalJackpotElements.length) {
        getTotal([...totalJackpotElements]);
    }

    jackpotsRoots.forEach((root) => {
        getJackpots(root);
    });
};

documentReady(() => {
    initJackpots();
});
